import {
  useEffect,
  useRef,
  cloneElement,
  isValidElement,
  Fragment,
  memo,
  useMemo,
  useCallback,
} from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
  TableContainer,
  Table,
  TablePagination,
  Paper,
  Tooltip,
  IconButton,
  Typography,
  Toolbar,
  TableProps,
  TableHeadProps,
} from '@mui/material';
import {
  useDispatch,
  useSelector,
  shallowEqual,
  fetchList,
  updateListParams,
  populateList,
  uiSelectors,
} from '../../state';
import {
  useIsTablet,
  useGetQueryParams,
  useUpdatePageQueryParams,
  useLoader,
} from '../../hooks';
import { FilterListIcon } from '../../assets';
import { getDefaultListParams } from '../../RegisterLists';
import { stableObject } from '../../lib';
import { ListBody } from './ListBody';
import { ListFiltersActions } from './filters/ListFiltersActions';
import { ListLoading } from './ListLoading';
import { ListToolbar } from './ListToolbar';
import { ListAside } from './ListAside';
import { useStyles } from './list.styles';
import type {
  ListActionComponentProps,
  FiltersProps,
  ObjectWithId,
  ListProps,
  ListBodyProps,
} from '../../types';
import {
  getColumnDefinitions,
  getColumnVisibilityMap,
  getVisibleColumns,
  loadStoredColumnVisibility,
  saveColumnVisibility,
} from './columnVisibility';
import { ColumnVisibilityMenu } from './ColumnVisibilityMenu';

export const List = <TRecord extends ObjectWithId = ObjectWithId>({
  body = <ListBody />,
  children,
  rowsPerPage: _rowsPerPage,
  order: _order = 'asc',
  orderBy: _orderBy = 'id',
  tableProps: _tableProps,
  headerProps: _headerProps,
  rowProps,
  cellProps,
  rootClassName,
  paperClassName,
  expand,
  expandOptions,
  filter: _filter,
  aside,
  title,
  bulkActionButtons,
  bulkActionOptions,
  // onToggleItem,
  resource,
  baseUrl,
  rowClick,
  mouseEnter,
  mouseLeave,
  rowStyle,
  rowsPerPageOptions,
  sortable = true,
  hideHeader = false,
  hideFooter = false,
  hideActions = false,
  actions = (
    <ListActions hideHeader={hideHeader} resource={resource} title={title} />
  ),
  listInfo,
  hasCustomViews = false,
  hasDefaultSearchField = true,
  defaultSearchProps,
  searchStyle,
  filtersProps,
  defaultState,
  customViewVersion,
  clearStateOnExit,
  scrollToTop = true,
  shouldUpdateQueryParams = true,
  shouldFetch = true,
  shouldSetUILoading = true,
  stickyHeader = false,
  rowTransform,
}: ListProps<TRecord>) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const isTablet = useIsTablet();
  const setLoader = useLoader();
  const updatePageQueryParams = useUpdatePageQueryParams();
  const {
    'aside-open': asideOpen,
    rowsPerPage: queryRowsPerPage,
    page: queryPage,
    orderBy: queryOrderBy,
    order: queryOrder,
    ...params
  } = useGetQueryParams();
  const isSidebarOpen = useSelector(uiSelectors.isSidebarOpen);
  const listLoading = useSelector((state) => state.ui.listLoading);
  const viewVersion = useSelector(
    (state) => customViewVersion ?? state.ui.viewVersion,
  ); // needed to refresh on edit
  const ids = useSelector(
    (state) =>
      (state.lists.resources[resource]?.ids || []) as Array<number | string>,
    shallowEqual,
  );
  const rows = useSelector(
    (state) =>
      (state.lists.resources[resource]?.data || {}) as Record<string, TRecord>,
    shallowEqual,
  );
  const listParams = useSelector(
    (state) => state.lists.resources[resource]?.params || {},
    shallowEqual,
  );
  const columnVisibility =
    listParams.columnVisibility || (stableObject as Record<string, boolean>);
  const selectedIds = useSelector(
    (state) => state.lists.resources[resource]?.params?.selectedIds || [],
    shallowEqual,
  );
  const selectedRows = useSelector(
    (state) => state.lists.resources[resource]?.params?.selectedRows || {},
    shallowEqual,
  );

  const {
    hasList,
    order,
    orderBy,
    page,
    queryParams,
    rowsPerPage,
    numberOfRows,
  } = listParams;

  const dataGridFields = useMemo<React.ReactNode>(() => {
    if (isValidElement(children)) {
      return (children as React.ReactElement<any>).props?.children;
    }
    return undefined;
  }, [children]);

  const columnDefinitions = useMemo(
    () => getColumnDefinitions(dataGridFields),
    [dataGridFields],
  );

  const resolvedColumnVisibility = useMemo(
    () => getColumnVisibilityMap(columnDefinitions, columnVisibility),
    [columnDefinitions, columnVisibility],
  );

  const visibleFields = useMemo(
    () => getVisibleColumns(columnDefinitions, resolvedColumnVisibility),
    [columnDefinitions, resolvedColumnVisibility],
  );

  const dataGridElement = useMemo(() => {
    if (!isValidElement(children)) {
      return children;
    }
    return cloneElement(children, undefined, visibleFields);
  }, [children, visibleFields]);

  const tableProps = _tableProps || (stableObject as TableProps);
  const headerProps = _headerProps || (stableObject as TableHeadProps);
  const filter = _filter || (stableObject as FiltersProps);

  const effectiveBodyData = useMemo(() => {
    if (!rowTransform || !ids.length) return { rows, ids };
    return rowTransform(rows, ids);
  }, [rowTransform, rows, ids]);

  const isFirstRender = useRef(true);
  const isFirstFetch = useRef(true);

  useEffect(() => {
    if (!rowsPerPage) {
      const defaultRowsPerPage = _rowsPerPage ?? 50;

      dispatch(
        updateListParams({
          list: resource,
          params: {
            order: queryOrder || _order,
            orderBy: queryOrderBy || _orderBy,
            rowsPerPage: queryRowsPerPage
              ? +queryRowsPerPage
              : defaultRowsPerPage,
            page: queryPage ? +queryPage - 1 : 0,
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_order, _orderBy, _rowsPerPage, dispatch, resource, rowsPerPage]);

  // `filter` is causing the useEffect to render too many times.
  // See https://github.com/kentcdodds/use-deep-compare-effect
  useDeepCompareEffect(() => {
    (async function () {
      if (!shouldFetch) {
        isFirstRender.current = false;
        return;
      }
      // reset page if filter changes
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      if (page > 0) {
        dispatch(
          updateListParams({
            list: resource,
            params: {
              page: 0,
            },
          }),
        );
      } else {
        setLoader(shouldSetUILoading);
        await dispatch(
          fetchList({
            resource,
            baseUrl,
            order,
            orderBy,
            page,
            rowsPerPage,
            filter,
            noPagination: hideFooter,
            sortable,
          }),
        );
        if (shouldSetUILoading) {
          setLoader(false);
        }
      }
    })();
  }, [filter]);

  /**
   * This effect runs only on the first render or when the pagination changes.
   * When the filters change then we use the above effect to reset the page.
   */
  useEffect(() => {
    (async function () {
      if (!shouldFetch) return;

      setLoader(shouldSetUILoading);
      const orderValue = sortable ? order : undefined;
      const orderByValue = sortable ? orderBy : undefined;
      const pageValue = !hideFooter && page ? +page + 1 : undefined;
      const rowsPerPageValue = !hideFooter ? rowsPerPage : undefined;
      let defaultFilters: Record<string, any> | undefined;

      // If there are default filters then we add them to the query params to trigger that effect to fetch the list
      if (isFirstFetch.current && !!rowsPerPage) {
        filtersProps?.filters?.forEach((f) => {
          // check if the default filters are already in the query params. If so we don't add them because if we do add them then if they all
          // exist then it won't trigger the effect to fetch the list and since we don't fetch the list here, the page won't load
          if (f.defaultValue && f.queryParam && !params[f.queryParam]) {
            defaultFilters = defaultFilters || {};
            defaultFilters[f.queryParam] = f.defaultValue;
          }
          if (
            f.defaultValues?.from &&
            f.queryParams?.fromParam &&
            !params[f.queryParams.fromParam ?? '']
          ) {
            defaultFilters = defaultFilters || {};
            defaultFilters[f.queryParams.fromParam] = f.defaultValues.from;
          }
          if (
            f.defaultValues?.to &&
            f.queryParams?.toParam &&
            !params[f.queryParams.toParam]
          ) {
            defaultFilters = defaultFilters || {};
            defaultFilters[f.queryParams.toParam] = f.defaultValues.to;
          }
        });

        isFirstFetch.current = false;
      }

      // If there are default filters then we add them to the query params to trigger that effect to fetch the list
      // so we don't call fetchList here
      if (!defaultFilters || !shouldUpdateQueryParams) {
        await dispatch(
          fetchList({
            resource,
            baseUrl,
            order,
            orderBy,
            page,
            rowsPerPage,
            filter,
            noPagination: hideFooter,
            sortable,
          }),
        );
      }
      if (shouldUpdateQueryParams) {
        updatePageQueryParams({
          order: orderValue,
          orderBy: orderByValue,
          page: pageValue,
          rowsPerPage: rowsPerPageValue,
          ...defaultFilters,
        });
      }
      if (shouldSetUILoading) {
        setLoader(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, order, orderBy, page, resource, rowsPerPage, shouldFetch, viewVersion]);

  useEffect(() => {
    if (!resource || !columnDefinitions.length) {
      return;
    }
    const hasMissingColumn = columnDefinitions.some(
      (column) =>
        !Object.prototype.hasOwnProperty.call(columnVisibility, column.key),
    );
    if (hasMissingColumn) {
      const storedVisibility = loadStoredColumnVisibility(resource);
      const merged = getColumnVisibilityMap(columnDefinitions, {
        ...storedVisibility,
        ...columnVisibility,
      });
      dispatch(
        updateListParams({
          list: resource,
          params: { columnVisibility: merged },
        }),
      );
    }
  }, [columnDefinitions, columnVisibility, dispatch, resource]);

  useEffect(() => {
    if (!resource || Object.keys(columnVisibility).length === 0) {
      return;
    }
    saveColumnVisibility(resource, columnVisibility);
  }, [resource, columnVisibility]);

  useEffect(() => {
    return () => {
      clearStateOnExit &&
        dispatch(
          populateList({
            list: resource,
            data: getDefaultListParams(resource),
          }),
        );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearStateOnExit, resource]);

  const handleChangePage = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
      newPage: number,
    ) => {
      dispatch(
        updateListParams({
          list: resource,
          params: { page: newPage },
        }),
      );
      if (shouldUpdateQueryParams) {
        updatePageQueryParams({ page: +newPage + 1 });
      }
      scrollToTop && window.scrollTo(0, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, resource, scrollToTop],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch(
        updateListParams({
          list: resource,
          params: { page: 0, rowsPerPage: parseInt(event.target.value, 10) },
        }),
      );
      if (shouldUpdateQueryParams) {
        updatePageQueryParams({
          page: 1,
          rowsPerPage: parseInt(event.target.value, 10),
        });
      }
      scrollToTop && window.scrollTo(0, 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resource, scrollToTop],
  );

  const tableContainerClasses = useMemo(
    () => ({
      root: cx(classes.tableContainer, {
        [classes.tableContainerSidebarOpen]: isSidebarOpen,
        [classes.tableContainerSticky]: stickyHeader,
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      classes.tableContainer,
      classes.tableContainerSidebarOpen,
      classes.tableContainerSticky,
      isSidebarOpen,
      stickyHeader,
    ],
  );

  const tablePaginationClasses = useMemo(
    () => ({
      selectLabel: classes.pagination,
      displayedRows: classes.pagination,
      select: classes.pagination,
    }),
    [classes.pagination],
  );

  // Split bodyProps into stable and dynamic parts to reduce re-renders
  const stableBodyProps = useMemo(
    () => ({
      rowProps,
      cellProps,
      rowClick,
      mouseEnter,
      mouseLeave,
      hasBulkActions: bulkActionButtons !== false,
      expand,
      expandOptions,
      resource,
      rowStyle,
      headerProps,
      bulkActionButtons,
      bulkActionOptions,
      hideHeader,
    }),
    [
      bulkActionButtons,
      bulkActionOptions,
      cellProps,
      expand,
      expandOptions,
      headerProps,
      hideHeader,
      mouseEnter,
      mouseLeave,
      resource,
      rowClick,
      rowProps,
      rowStyle,
    ],
  );

  const bodyProps = useMemo<Partial<ListBodyProps>>(
    () => ({
      ...stableBodyProps,
      rows: effectiveBodyData.rows,
      ids: effectiveBodyData.ids,
      selectedIds,
      selectedRows,
      rowsPerPage,
      page,
      numSelected: selectedIds.length,
      order,
      orderBy,
      rowCount: effectiveBodyData.ids.length,
    }),
    [
      stableBodyProps,
      effectiveBodyData,
      selectedIds,
      selectedRows,
      rowsPerPage,
      page,
      order,
      orderBy,
    ],
  );

  return (
    <Fragment>
      <div className={cx(classes.root, rootClassName)}>
        {hasList &&
          shouldFetch &&
          !hideActions &&
          isValidElement(actions) &&
          cloneElement(
            actions as React.ReactElement<ListActionComponentProps<TRecord>>,
            {
              resource,
              title,
              classes,
              rows,
              ids,
              selectedIds,
              selectedRows,
              fields: dataGridFields,
              columnVisibility: resolvedColumnVisibility,
              loading: listLoading,
            },
          )}
        {shouldFetch && listLoading && !hasList && (
          <ListLoading
            classes={classes}
            className={paperClassName}
            // expand={expand}
            hasBulkActions={bulkActionButtons !== false}
            nbChildren={visibleFields.length}
            size={tableProps.size || isTablet ? 'small' : 'medium'}
          />
        )}
        {(hasList || !shouldFetch) && (
          <div className={classes.content}>
            <Paper
              className={cx(
                classes.paper,
                { [classes.asideOpen]: !!asideOpen },
                paperClassName,
              )}
              elevation={0}
            >
              {(hasDefaultSearchField || !!filtersProps) && (
                <ListFiltersActions
                  defaultSearchProps={defaultSearchProps}
                  queryParams={queryParams}
                  order={order}
                  orderBy={orderBy}
                  filtersProps={filtersProps}
                  hasCustomViews={hasCustomViews}
                  hasDefaultSearchField={hasDefaultSearchField}
                  resource={resource}
                  searchStyle={searchStyle}
                  fields={dataGridFields}
                  columnVisibility={resolvedColumnVisibility}
                />
              )}
              {!hasDefaultSearchField && !filtersProps && (
                <div className={classes.listControls}>
                  <ColumnVisibilityMenu
                    resource={resource}
                    fields={dataGridFields}
                    columnVisibility={resolvedColumnVisibility}
                  />
                </div>
              )}
              {isValidElement(listInfo) && cloneElement(listInfo)}
              {shouldFetch && (
                <ListToolbar
                  numSelected={selectedIds.length}
                  ids={ids}
                  rows={rows}
                  selectedIds={selectedIds}
                  selectedRows={selectedRows}
                  bulkActionButtons={bulkActionButtons}
                  numberOfRows={numberOfRows}
                  resource={resource}
                  baseUrl={baseUrl}
                  filter={filter}
                  order={order}
                  orderBy={orderBy}
                  sortable={sortable}
                />
              )}
              {shouldFetch && effectiveBodyData.ids?.length ? (
                <Fragment>
                  <TableContainer classes={tableContainerClasses}>
                    <Table
                      // className={classes.table}
                      aria-labelledby='tableTitle'
                      aria-label='enhanced table'
                      size={tableProps.size || isTablet ? 'small' : 'medium'}
                      stickyHeader={stickyHeader}
                      {...tableProps}
                    >
                      <MemoizedListBody bodyProps={bodyProps} body={body}>
                        {dataGridElement}
                      </MemoizedListBody>
                    </Table>
                  </TableContainer>
                  {!hideFooter && (
                    <TablePagination
                      rowsPerPageOptions={
                        rowsPerPageOptions || [25, 50, 75, 100]
                      }
                      component='div'
                      count={numberOfRows}
                      rowsPerPage={rowsPerPage || 50}
                      page={page || 0}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      classes={tablePaginationClasses}
                    />
                  )}
                </Fragment>
              ) : isValidElement(defaultState) ? (
                cloneElement(defaultState)
              ) : (
                <Typography style={{ padding: 24 }}>
                  No results found
                </Typography>
              )}
            </Paper>
            {isValidElement(aside) &&
              cloneElement(<ListAside />, { rows, ids }, aside)}
          </div>
        )}
      </div>
    </Fragment>
  );
};

const MemoizedListBody = memo(
  ({
    bodyProps,
    body,
    children,
  }: {
    bodyProps: Partial<ListBodyProps>;
    body: React.ReactElement;
    children: React.ReactNode;
  }) => {
    return cloneElement(body, bodyProps, children);
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Only re-render if rows data actually changed, not just reference
    return (
      prevProps.bodyProps.order === nextProps.bodyProps.order &&
      prevProps.bodyProps.orderBy === nextProps.bodyProps.orderBy &&
      prevProps.bodyProps.page === nextProps.bodyProps.page &&
      prevProps.bodyProps.rowsPerPage === nextProps.bodyProps.rowsPerPage &&
      prevProps.bodyProps.ids === nextProps.bodyProps.ids &&
      prevProps.bodyProps.rows === nextProps.bodyProps.rows &&
      prevProps.bodyProps.selectedIds === nextProps.bodyProps.selectedIds &&
      prevProps.children === nextProps.children
    );
  },
);

const ListActions = memo(function ListActions({
  title,
  resource,
  hideHeader,
}: {
  title?: string;
  resource: string;
  hideHeader: boolean;
}) {
  const { classes } = useStyles();
  if (hideHeader) return null;

  return (
    <Toolbar className={classes.actions}>
      <Typography
        className={classes.title}
        variant='h6'
        id='tableTitle'
        component='div'
      >
        {title || resource}
      </Typography>
      <Tooltip title='Filter list'>
        <IconButton aria-label='filter list' size='large'>
          <FilterListIcon />
        </IconButton>
      </Tooltip>
    </Toolbar>
  );
});
